import React, { useRef, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import UserSidebar from "@/components/ui/UserSidebar";
import UserHeader from "@/components/ui/UserHeader";
import AdminSidebar from "@/components/admin-dashboard/AdminSidebar";
import AdminHeader from "@/components/admin-dashboard/AdminHeader";
import CohortSidebar from "@/components/admin-dashboard/CohortSidebar";
import { ROLES, Role } from "@/constants/roles";
import { useAuth } from "@/contexts/AuthContext";
import TrainerSidebar from "@/components/ui/TrainerSidebar";
import TrainerHeader from "@/components/ui/TrainerHeader";
import { useSidebarStore } from "@/store/sidebarStore";
import { useHeaderStore } from "@/store/headerStore";
import LearningAreaSidebar from "@/components/ui/LearningAreaSidebar";
import clsx from "clsx";
import { getTrainerActiveCohort, clearTrainerActiveCohort } from "@/utils/session";

interface LayoutProps {
  children: React.ReactNode;
  title: string;
  showSidebar?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, showSidebar = true }) => {
  const { currentUser } = useAuth();
  const userRole = (currentUser?.role as Role) || ROLES.USER;
  const location = useLocation();
  const { isOpen } = useSidebarStore();
  const { isFitToScreen } = useHeaderStore();

  // Ref for content
  const contentRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Check if we're on a cohort dashboard route
  const isCohortDashboardRoute =
    location.pathname.startsWith("/cohort-dashboard/");

  // Check if we're on the financial statements page
  const isFinancialStatementsRoute =
    location.pathname === "/financial-statements";

  // Check if we're on the learning area page
  const isLearningAreaRoute = location.pathname === "/learning-area";

  // Check if we're on the transaction summary page
  const isTransactionSummaryRoute =
    location.pathname === "/transaction-summary";

  // Pages where trainer should see participant UI (course view sub-pages)
  const trainerParticipantPages = ["/transaction-summary", "/financial-statements", "/learning-area"];
  const isTrainerOnParticipantSubPage =
    userRole === ROLES.TRAINER &&
    trainerParticipantPages.includes(location.pathname) &&
    !!getTrainerActiveCohort();

  // Check if trainer is viewing a course as participant (including sub-pages)
  const isTrainerCourseRoute =
    userRole === ROLES.TRAINER &&
    (location.pathname.startsWith("/course/") || isTrainerOnParticipantSubPage);

  // Clear trainer active cohort when navigating to trainer management routes
  useEffect(() => {
    if (userRole === ROLES.TRAINER && !isTrainerCourseRoute) {
      clearTrainerActiveCohort();
    }
  }, [userRole, isTrainerCourseRoute]);

  // Only return children for pages that handle their own layout
  if (isTransactionSummaryRoute || isFinancialStatementsRoute) {
    return <>{children}</>;
  }

  const renderSidebar = () => {
    if (!showSidebar) return null;

    // Trainer viewing course as participant — show user sidebar
    if (isTrainerCourseRoute) {
      return (
        <UserSidebar
          isSidebarOpen={isOpen}
          setIsSidebarOpen={useSidebarStore.getState().toggle}
        />
      );
    }

    // If we're on the learning area page, show the learning area sidebar
    if (isLearningAreaRoute) {
      return (
        <LearningAreaSidebar
          isSidebarOpen={isOpen}
          setIsSidebarOpen={useSidebarStore.getState().toggle}
        />
      );
    }

    // If we're on a cohort dashboard route, show CohortSidebar for admin/trainer
    if (
      isCohortDashboardRoute &&
      (userRole === ROLES.ADMIN || userRole === ROLES.TRAINER)
    ) {
      return <CohortSidebar />;
    }

    // Otherwise, show the normal role-based sidebar
    switch (userRole) {
      case ROLES.ADMIN:
        return <AdminSidebar />;
      case ROLES.USER:
        return (
          <UserSidebar
            isSidebarOpen={isOpen}
            setIsSidebarOpen={useSidebarStore.getState().toggle}
          />
        );
      case ROLES.TRAINER:
      default:
        return <TrainerSidebar title={''} isOpen={false} />;
    }
  };

  const renderHeader = () => {
    // Trainer viewing course as participant — show user header
    if (isTrainerCourseRoute) {
      return (
        <UserHeader onToggleSidebar={useSidebarStore.getState().toggle} />
      );
    }

    // For cohort dashboard routes, show AdminHeader (which will show cohort breadcrumbs)
    if (isCohortDashboardRoute && (userRole === ROLES.ADMIN || userRole === ROLES.TRAINER)) {
      return <AdminHeader />;
    }

    switch (userRole) {
      case ROLES.ADMIN:
        return <AdminHeader />;
      case ROLES.USER:
        return (
          <UserHeader
            onToggleSidebar={useSidebarStore.getState().toggle}
          />
        );
      case ROLES.TRAINER:
      default:
        return <TrainerHeader title={''} />;
    }
  };

  // Check if we're on the dashboard page (where fit-to-screen applies)
  const isDashboardRoute = location.pathname === "/dashboard" || isTrainerCourseRoute;
  const shouldApplyFitToScreen = isFitToScreen && isDashboardRoute && (userRole === ROLES.USER || isTrainerCourseRoute);

  // Trainer on course route should get user-style sidebar padding
  const hasUserSidebar = userRole === ROLES.USER || isTrainerCourseRoute;

  return (
    <div className="min-h-screen bg-gray-100 flex font-metropolis">
      {renderSidebar()}

      <div
        className={clsx(
          "flex-1 transition-all duration-300 ease-in-out bg-white",
          // The user sidebar is always visible at lg+ (isOpen only hides it below lg),
          // so the padding must not depend on isOpen — otherwise the sidebar covers the Assets column.
          showSidebar && hasUserSidebar && "lg:pl-[260px] 2xl:pl-[290px] min-1660:pl-[320px]",
          shouldApplyFitToScreen && "overflow-hidden h-screen"
        )}
      >
        {renderHeader()}
        <main
          ref={mainRef}
          className={clsx(
            "bg-white",
            shouldApplyFitToScreen
              ? "h-screen overflow-auto p-2"
              : "p-4 2xl:p-6 h-2md:pb-0"
          )}
        >
          <div ref={contentRef}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
